"use client";

import { useInstantSearch } from "react-instantsearch";
import { useRouter } from "next/navigation";

interface SearchStateManagerProps {
  children: React.ReactNode;
}

export function SearchStateManager({ children }: SearchStateManagerProps) {
  const { uiState, setUiState } = useInstantSearch();
  const router = useRouter();

  // Function to capture current search state and navigate to profile
  const navigateToProfileWithState = (creatorId: string) => {
    // Store the current UI state in sessionStorage
    const searchState = {
      uiState,
      timestamp: Date.now(),
    };

    sessionStorage.setItem("meilisearch_state", JSON.stringify(searchState));

    // Navigate to profile
    router.push(`/v1/search/advanced/creator/${creatorId}`);
  };

  // Function to restore search state when returning
  const restoreSearchState = () => {
    const storedState = sessionStorage.getItem("meilisearch_state");

    if (storedState) {
      try {
        const { uiState: savedUiState, timestamp } = JSON.parse(storedState);

        // Only restore if the state is less than 1 hour old
        const oneHour = 60 * 60 * 1000;
        if (Date.now() - timestamp < oneHour) {
          setUiState(savedUiState);
        }
      } catch (error) {
        console.warn("Failed to restore search state:", error);
      }
    }
  };

  // Expose functions through context or props
  return <div data-search-state-manager>{children}</div>;
}

// Hook to use search state management
export function useSearchStateNavigation() {
  const { uiState } = useInstantSearch();
  const router = useRouter();

  const navigateToProfile = (creatorId: string) => {
    // Store current state including pagination
    const searchState = {
      uiState,
      timestamp: Date.now(),
      currentPath: window.location.pathname + window.location.search,
      // Store additional metadata for better restoration
      metadata: {
        totalHits: document.querySelectorAll("[data-creator-card]").length,
        scrollPosition: window.scrollY,
      },
    };

    sessionStorage.setItem("meilisearch_state", JSON.stringify(searchState));

    // Navigate with return URL as backup
    const returnUrl = encodeURIComponent(
      window.location.pathname + window.location.search
    );
    router.push(
      `/v1/search/advanced/creator/${creatorId}?returnUrl=${returnUrl}`
    );
  };

  return { navigateToProfile };
}
